import React, { useState, useMemo, useEffect } from 'react';
import styles from './TransportationServiceVehicleSelector.module.scss';
import GenericSelect, { GenericSelectOption } from '../GenericSelect/GenericSelect';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import clsx from 'clsx';
import { Spinner } from 'components/UI';
import { Vehicle } from '@spike/vehicle-model';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';

interface Props {
    loading?: boolean;
    value?: Vehicle;
    onChange?: (value: Vehicle) => void;
    className?: string;
}

export const TransportationServiceVehicleSelector: React.FC<Props> = ({ value, onChange, className, loading }) => {
    const vehicles = useSelector<RootState, Vehicle[] | undefined>(state => state.vehicles.vehicles);

    const [selected, setSelected] = useState<Vehicle | undefined>(value);

    useEffect(() => {
        setSelected(value);
    }, [value]);

    const options: GenericSelectOption[] = useMemo(() => {
        if (!vehicles) return [];

        return vehicles.map(s => ({
            id: s.id!,
            label: s.name
        }));
    }, [vehicles]);

    const handleSelect = (opt: GenericSelectOption) => {
        const selectedVehicle = vehicles?.find((op: Vehicle) => op.id === opt.id);
        setSelected(selectedVehicle);
        onChange?.(selectedVehicle!);
    };

    const selectedOption = options.find(o => o.id === selected?.id) ?? null;

    const emptyServicesComponent = (
        <div id="booking_div_no_services_alert">
            <label className={styles.title}>Vehicle</label>
            <AlertNewBooking
                className={styles.alert}
                bWidth="2px"
                icon={faExclamationTriangle}
                iconSize="lg"
                iconColor="#EAB464"
                bgColor="#F8F5F1"
                bdColor="#BCB8AE"
            >
                <p className={styles.alertText}>No vehicles available for this service</p>
            </AlertNewBooking>
        </div>
    );

    return (
        <div className={clsx(styles.container, className)}>
            {loading && <Spinner />}
            {!loading && (!vehicles || vehicles?.length === 0) && emptyServicesComponent}
            {!loading && vehicles && vehicles?.length > 0 && (
                <>
                    <label className={styles.title}>Vehicle</label>
                    <GenericSelect
                        placeholder="Select Vehicle"
                        selected={selectedOption}
                        options={options}
                        onSelect={handleSelect}
                        className={styles.select}
                    />
                </>
            )}
        </div>
    );
};

export default TransportationServiceVehicleSelector;
